import DashboardLayout from "@/views/Layout/DashboardLayout.vue";
import AuthLayout from "@/views/Pages/AuthLayout.vue";
// GeneralViews
import NotFound from "@/views/GeneralViews/NotFoundPage.vue";
// Legacy /app-catalog paths -> /seller (spec docs/specs/2026-10-03-seller-ui-revamp-design.md §2).
// The rewrite lives in one pure function so the legacy redirect below, the
// sidebar and the pages cannot disagree about it.
import { sellerPath } from "@/utils/sellerProductRow";
import { marketingHomePath } from "@/utils/affiliate";

// ============================================================
// Example Component
// ============================================================
// Calendar
const Calendar = () =>
    import(/* webpackChunkName: "extra" */ "@/views/Calendar/Calendar.vue");
// Charts
const Charts = () =>
    import(/* webpackChunkName: "dashboard" */ "@/views/Charts.vue");

// Components pages
const Buttons = () =>
    import(
        /* webpackChunkName: "components" */ "@/views/Components/Buttons.vue"
    );
const Cards = () =>
    import(/* webpackChunkName: "components" */ "@/views/Components/Cards.vue");
const GridSystem = () =>
    import(
        /* webpackChunkName: "components" */ "@/views/Components/GridSystem.vue"
    );
const Notifications = () =>
    import(
        /* webpackChunkName: "components" */ "@/views/Components/Notifications.vue"
    );
const Icons = () =>
    import(/* webpackChunkName: "components" */ "@/views/Components/Icons.vue");
const Typography = () =>
    import(
        /* webpackChunkName: "components" */ "@/views/Components/Typography.vue"
    );

// Dashboard pages
const Dashboard = () =>
    import(
        /* webpackChunkName: "dashboard" */ "@/views/Dashboard/Dashboard.vue"
    );
const AlternativeDashboard = () =>
    import(
        /* webpackChunkName: "dashboard" */ "@/views/Dashboard/AlternativeDashboard.vue"
    );
const Widgets = () =>
    import(/* webpackChunkName: "dashboard" */ "@/views/Widgets.vue");

// Forms pages
const FormElements = () =>
    import(/* webpackChunkName: "forms" */ "@/views/Forms/FormElements.vue");
const FormComponents = () =>
    import(/* webpackChunkName: "forms" */ "@/views/Forms/FormComponents.vue");
const FormValidation = () =>
    import(/* webpackChunkName: "forms" */ "@/views/Forms/FormValidation.vue");

// Maps pages
const GoogleMaps = () =>
    import(/* webpackChunkName: "extra" */ "@/views/Maps/GoogleMaps.vue");
const VectorMaps = () =>
    import(/* webpackChunkName: "extra" */ "@/views/Maps/VectorMaps.vue");

// TableList pages
const RegularTables = () =>
    import(/* webpackChunkName: "tables" */ "@/views/Tables/RegularTables.vue");
const SortableTables = () =>
    import(
        /* webpackChunkName: "tables" */ "@/views/Tables/SortableTables.vue"
    );
const PaginatedTables = () =>
    import(
        /* webpackChunkName: "tables" */ "@/views/Tables/PaginatedTables.vue"
    );

// Pages
const User = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/UserProfile.vue");
const Pricing = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/Pricing.vue");
const TimeLine = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/TimeLinePage.vue");
const Login = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/Login.vue");
const Home = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/Home.vue");
const Register = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/Register.vue");
const Lock = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/Lock.vue");

const ProfileChangePassword = () =>
    import(/* webpackChunkName: "tables" */ "@/views/Profile/EditPasswordForm.vue");

const ProfileChangeProfile = () =>
    import(/* webpackChunkName: "tables" */ "@/views/Profile/EditProfileForm.vue");

const ProfileMenuSettings = () =>
    import(/* webpackChunkName: "tables" */ "@/views/Profile/MenuSettings.vue");

const ForgotPassword = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/ForgotPassword.vue");
const ResetPassword = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/ResetPassword.vue");

// LINE OAuth landing page (spec docs/specs/2026-10-03-bo-staff-line-login-design.md §5).
// Registered as a guest route under authPages: it is reached while LOGGED OUT
// for the login flow, so a requiresAuth guard here would bounce every
// "เข้าสู่ระบบด้วย LINE" straight back to /login before the code is exchanged.
const LineCallback = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Pages/LineCallback.vue");

// ============================================================
// Stock Component
// ============================================================
const storageList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/StorageTables.vue");
const partnerList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/PartnerTables.vue");
const productList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/ProductTables.vue");
const productListV2 = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/ProductTablesV2.vue");
const availableProductList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/AvailableProductTables.vue");
const availableProductPackingOrders = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/AvailableProductPackingOrders.vue");
const availableProductView = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/AvailableProductView.vue");
const productWaitShipList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/ProductWaitShipTables.vue");
const inventoryList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryTables.vue");
const inventoryDashboard = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryDashboard.vue");
const inventoryDashboardV2 = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryDashboardV2.vue");
const inventoryDetailList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryDetailTables.vue");
const InventoryHistoryByStorage = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryHistoryByStorage.vue");
const InventorySummaryPacking = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventorySummaryPacking.vue");
const promotionList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/PromotionTables.vue");

const productForecast = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/ProductForecastTables.vue");

const productUsage = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/ProductUsageTables.vue");
const shippedNoDeductReport = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/ShippedNoDeductReport.vue");

const inventoryIn = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryIn.vue");
const inventoryOut = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Stock/InventoryOut.vue");
const inventoryTransfer = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Stock/InventoryTransfer.vue"
    );
const inventoryTransferToLine = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Stock/InventoryTransferToLine.vue"
    );
const inventoryHistory = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Stock/InventoryHistoryTables.vue"
    );
const inventoryHistoryDetail = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Stock/InventoryHistoryDetail.vue"
    );
const returnWarehouse = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Stock/ReturnWarehouse.vue"
    );

// ============================================================
// Backoffice Component
// ============================================================
const boOrder = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/OrderTables.vue"
    );
// ภาพรวมรายการสั่งซื้อ (/backoffice/orders-overview, staff only) —
// docs/specs/2026-10-07-bo-orders-new-status-tabs-design.md §6.2,
// docs/specs/2026-10-09-bo-fn-report-form-and-orders-overview-design.md §B.
const boOrdersNew = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/OrdersNew.vue");
const boOrderReprocess = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/OrderReprocess.vue"
    );
const boOrderForm = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/OrderForm.vue");

const boOrderFormQuick = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/OrderFormQuick.vue");

const boProduct = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ProductTables.vue"
    );
const boPlatformProduct = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/PlatformProductTables.vue"
    );
const boPayment = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/PaymentTables.vue"
    );
const boPacking = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/PackingTables.vue"
    );
const boShipping = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingForm.vue"
    );
const boShippingWithCoupon = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingFormByLineAutoCloseWithCoupon.vue"
    );

const boShippingWithCouponV2 = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingFormByLineAutoCloseWithCouponV2.vue"
    );

const boShippingWithCouponV3 = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingFormByLineAutoCloseWithCouponV3.vue"
    );

const boShippingChecking = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingCheckingForm.vue"
    );
const boShippingByLine = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingFormByLine.vue"
    );
const boShippingByLineAutoClose = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingFormByLineAutoClose.vue"
    );

const boShippingAdmin = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingAdminForm.vue"
    );
const boShippingReturn = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingReturnForm.vue"
    );
const boShippingReturnV2 = () =>
		import(
			/* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingReturnFormV2.vue"
		);
const boShippingReturnV3 = () =>
		import(
			/* webpackChunkName: "pages" */ "@/views/Backoffice/ShippingReturnFormV3.vue"
		);
const boCustomer = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Backoffice/CustomerTables.vue"
    );

// ============================================================
// PO Component
// ============================================================
const boPO = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/PO/POTables.vue"
    );

const boPOForm = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/PO/POForm.vue"
    );

// ============================================================
// Coupon Component
// ============================================================
const boCoupon = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Coupon/CouponTables.vue"
    );

const boCouponDetail = () =>
        import(
            /* webpackChunkName: "pages" */ "@/views/Coupon/CouponDetailTables.vue"
        );
const boCouponDetailAll = () =>
        import(
            /* webpackChunkName: "pages" */ "@/views/Coupon/CouponDetailAllTables.vue"
        );
const boCouponLogs = () =>
        import(
            /* webpackChunkName: "pages" */ "@/views/Coupon/CouponLogTables.vue"
        );
const boCouponMember = () =>
        import(
            /* webpackChunkName: "pages" */ "@/views/Coupon/CouponMemberTables.vue"
        );


const boCouponForm = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Coupon/CouponForm.vue"
    );






// ============================================================
// Report Component
// ============================================================

const reportDashboard = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportDashboard.vue"
    );
const reportTeam = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportTeam.vue"
    );

const reportSaleChannel = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportSaleChannel.vue"
    );

const reportProduct = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportProduct.vue"
    );

const reportUser = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportUser.vue"
    );
const reportUserAndProduct = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportUserAndProduct.vue"
    );
const reportSaleChannelAndProduct = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportSaleChannelAndProduct.vue"
    );
const reportProductCost = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportProductCost.vue"
    );

const reportSummaryWmsProduct = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportSummaryWmsProduct.vue"
    );
const reportSummaryWms = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportSummaryWms.vue"
    );
const reportSummaryWmsByField = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportSummaryWmsByField.vue"
    );
const reportCommission = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportCommission.vue"
    );
	const reportOrderReturn = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportOrderReturn.vue"
    );
const reportDashboardByPlatformChannel = () => import(
        /* webpackChunkName: "pages" */ "@/views/Report/ReportDashboardByPlatformChannel.vue"
    );

// ============================================================
// Platform Component
// ============================================================
const platformConnect = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Platform/PlatformConnect.vue"
    );

const platformSyncLogs = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Platform/PlatformSyncLogs.vue"
    );

const platformTiktok = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Platform/TiktokOrderTables.vue"
    );

// ============================================================
// Support / Issue Report Component
// ============================================================
const issueReports = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Support/IssueReports.vue"
    );

const issueReportForm = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Support/IssueReportForm.vue"
    );

const releases = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Support/Releases.vue"
    );

// ============================================================
// Admin Component
// ============================================================
const adminUser = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/UserTables.vue");


const adminSaleChannel = () =>
    import(
        /* webpackChunkName: "pages" */ "@/views/Admin/SaleChannelTables.vue"
    );

const adminProduct = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/ProductTables.vue");

const adminSkuRandom = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/SkuRandomTables.vue");
const adminCoupon = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/CouponTables.vue");


const adminProductForm = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/ProductForm.vue");
const adminPlatformProduct = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/PlatformProductTables.vue");
const adminPlatformProductV2 = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/PlatformProductTableV2.vue");
const adminPlatformProductForm = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/PlatformProductForm.vue");

const adminPaymentChannel = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/PaymentChannelTables.vue");

// ============================================================
// Agent Commission Component
// ============================================================
const agentCommissionSettings = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/AgentCommissionSettings.vue");
const agentCommissionRounds = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/AgentCommissionRounds.vue");
const agentCommissionRoundDetail = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/AgentCommissionRoundDetail.vue");
const agentCommissionRemittance = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/AgentCommissionRemittance.vue");

// ============================================================
// Platform Statement Component
// ============================================================
const platformStatementList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/PlatformStatementList.vue");
const platformStatementUpload = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/PlatformStatementUpload.vue");
const platformStatementDetail = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/PlatformStatementDetail.vue");
const platformStatementSummary = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Backoffice/PlatformStatementSummary.vue");

// ============================================================
// Shipping Config Component
// ============================================================
const shippingConfig = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Admin/ShippingConfig.vue");

// ============================================================
// Account Component
// ============================================================
const accountItems = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Account/AccountItemTables.vue");
const accountRecords = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Account/AccountRecordTables.vue");
const accountReports = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Account/AccountReports.vue");

// ============================================================
// KOL Component
// ============================================================
const kolStaff = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLStaffTables.vue");
const kolCreators = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLCreatorTables.vue");
const kolAssignments = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLAssignmentTables.vue");
const kolReports = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLReports.vue");
const kolProductSends = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLProductSendTables.vue");
const kolPayoutBatches = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLPayoutBatches.vue");
const kolPayoutBatchDetail = () =>
    import(/* webpackChunkName: "pages" */ "@/views/KOL/KOLPayoutBatchDetail.vue");

// Member Management (customer support)
const memberAdminList = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Member/MemberList.vue");
const memberAdminDetail = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Member/MemberDetail.vue");
const memberAdminLevels = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Member/MemberLevels.vue");
// Coin liability report (coin D3-4)
const memberCoinsReport = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Member/MemberCoinsReport.vue");
// COD-rejection anti-abuse guard (issue #179)
const memberCodRejections = () =>
    import(/* webpackChunkName: "pages" */ "@/views/Member/CodRejections.vue");

// ============================================================
// App Catalog Component
// ============================================================
const appCatalogCategories = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/Categories.vue");
const appCatalogProducts = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/Products.vue");
const appCatalogProductEdit = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/ProductEdit.vue");
const appCatalogPromotions = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/Promotions.vue");
// แฟลชเซล create/edit as its own page (spec 2026-10-05-flash-sale-campaign-page §2).
const appCatalogFlashSaleEdit = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/FlashSaleEdit.vue");
const appCatalogBundles = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/PromotionBundles.vue");
const appCatalogBundleEdit = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/PromotionBundleEdit.vue");
const appCatalogCouponTemplates = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/CouponTemplates.vue");
const appCatalogStorefront = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/StorefrontSettings.vue");
// #185 — storefront homepage content editor (12 CMS sections)
const appCatalogStorefrontHome = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/StorefrontHomeContent.vue");
const appCatalogStorefrontCollections = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/StorefrontCollections.vue");
const appCatalogWmsProducts = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/WmsProductList.vue");
const appCatalogShippingTiers = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/ShippingTiers.vue");
// #263 — seller category -> platform category mapping (spec §6B.8).
const appCatalogPlatformCategoryMap = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/PlatformCategoryMap.vue");
// #282 — Shopee global category -> seller category mapping (spec §6D.6).
const appCatalogShopeeCategoryMap = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/ShopeeCategoryMap.vue");
// B7 — Shopee promotions (Discount + Shop Flash Sale) mirrored into the app's
// per-SKU flash window (spec 2026-10-02-shopee-promotions-to-seller §6).
const appCatalogShopeePromotions = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/ShopeePromotions.vue");
// #301 — SKUs whose WMS mapping is unusable, so their stock never follows the
// warehouse (shared stock spec §8/§11 FE-1).
const appCatalogStockUnmatched = () =>
    import(/* webpackChunkName: "app-catalog" */ "@/views/AppCatalog/StockUnmatched.vue");

// Seller views (#46/#48/#50). Orders.vue is a stub owned by Dev 2.
const sellerOrders = () =>
    import(/* webpackChunkName: "seller" */ "@/views/Seller/Orders.vue");
const sellerPaymentReport = () =>
    import(/* webpackChunkName: "seller" */ "@/views/Seller/PaymentReport.vue");

// ============================================================
// Modern Trade Component
// ============================================================
// #443 POS หน้าร้าน (spec 2026-10-10 §7)
const posDevices = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosDevices.vue");
const posEvents = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosEvents.vue");
const posEventForm = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosEventForm.vue");
const posProducts = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosProducts.vue");
const posSettings = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosSettings.vue");
const posBills = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosBills.vue");
const posReportSales = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosReportSales.vue");
const posReportTopSpenders = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosReportTopSpenders.vue");
const posReportCustomer = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosReportCustomer.vue");
const posReportProducts = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosReportProducts.vue");
const posReportPromotions = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosReportPromotions.vue");
const posReportEventMembers = () =>
    import(/* webpackChunkName: "pos" */ "@/views/Pos/PosReportEventMembers.vue");

const mtCompanies = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtCompanies.vue");
const mtReceipts = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtReceipts.vue");
const mtReceiptDetail = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtReceiptDetail.vue");
const mtSalesImport = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtSalesImport.vue");
const mtStock = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtStock.vue");
const mtExpiryReport = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtExpiryReport.vue");
const mtExpiryReturns = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtExpiryReturns.vue");
// Modern Trade v2 (epic mdf-project#144+)
const mtProducts = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtProducts.vue");
const mtPriceTemplates = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtPriceTemplates.vue");
const mtSaleOrders = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtSaleOrders.vue");
const mtSaleOrderDetail = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtSaleOrderDetail.vue");
// Full-page PO editor — create + edit draft (2026-11-03 spec §2)
const mtSaleOrderEdit = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtSaleOrderEdit.vue");
const mtInvoices = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtInvoices.vue");
// ฝากขาย — รับเงินผูกกับบริษัทคู่ค้า (spec 2026-08-13)
const mtConsignmentPayments = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtConsignmentPayments.vue");
const mtSalesReport = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtSalesReport.vue");
const mtStockValueReport = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtStockValueReport.vue");
// #166 — Document format settings
const mtDocFormatSettings = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtDocFormatSettings.vue");
// round-19 Dev-B — MT audit log
const mtAuditLog = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtAuditLog.vue");
// #183 — full-screen print-set preview (SO/MO/DO/IV). Registered as a TOP-LEVEL
// route below (not a modernTradeMenu child) so it renders without the DashboardLayout sidebar.
const mtPrintPreview = () =>
    import(/* webpackChunkName: "modern-trade" */ "@/views/ModernTrade/MtPrintPreview.vue");

// #169 — BO admin promo-notification composer
const promoNotificationAdmin = () =>
    import(/* webpackChunkName: "notification" */ "@/views/Notification/PromoNotificationAdmin.vue");

let wmsMenu = {
    path: "/wms",
    component: DashboardLayout,
    redirect: "/wms/inventory",
    name: "wms",
    meta: {
        breadcrumb: "wms.name"
    },
    children: [
        {
            path: "promotion",
            name: "WmsPromotion",
            component: promotionList,
            meta: {
                breadcrumb: "wms.menu.promotion",
                requiresAuth: true
            }
        },
        {
            path: "history",
            name: "WmsInventoryHistory",
            component: inventoryHistory,
            meta: {
                breadcrumb: "wms.menu.inventory_history",
                requiresAuth: true
            }
        },
        {
            path: "historydetail/:id",
            name: "WmsInventoryHistoryDetail",
            component: inventoryHistoryDetail,
            meta: {
                breadcrumb: "wms.menu.inventory_history",
                requiresAuth: true
            }
        },
        {
            path: "inventory/dashboardv1",
            name: "WmsInventoryDashboardv1",
            component: inventoryDashboard,
            meta: {
                breadcrumb: "wms.menu.inventory_dashboard",
                requiresAuth: true
            }
        },
        {
            path: "inventory/dashboard",
            name: "WmsInventoryDashboardV2",
            component: inventoryDashboardV2,
            meta: {
                breadcrumb: "wms.menu.inventory_dashboard",
                requiresAuth: true
            }
        },
        {
            path: "inventory/summary",
            name: "WmsInventory",
            component: inventoryList,
            meta: {
                breadcrumb: "wms.menu.inventory_list",
                requiresAuth: true
            }
        },
        {
            path: "inventory/detail",
            name: "WmsInventoryDetail",
            component: inventoryDetailList,
            meta: {
                breadcrumb: "wms.menu.inventory_list_detail",
                requiresAuth: true
            }
        },
        {
            path: "inventory/storage",
            name: "WmsInventoryHistoryByStorage",
            component: InventoryHistoryByStorage,
            meta: {
                breadcrumb: "wms.menu.inventory_history_storage_list",
                requiresAuth: true
            }
        },
        {
            path: "inventory/packing",
            name: "WmsInventorySummaryPacking",
            component: InventorySummaryPacking,
            meta: {
                breadcrumb: "wms.menu.inventory_summary_packing",
                requiresAuth: true
            }
        },
        {
            path: "inventory/in",
            name: "WmsInventoryIn",
            component: inventoryIn,
            meta: {
                breadcrumb: "wms.menu.inventory",
                requiresAuth: true
            }
        },
        {
            path: "inventory/out",
            name: "WmsInventoryOut",
            component: inventoryOut,
            meta: {
                breadcrumb: "wms.menu.inventory",
                requiresAuth: true
            }
        },
        {
            // #437 — report that feeds เบิกสินค้า (inventory/out) above.
            path: "inventory/shipped-no-deduct",
            name: "WmsShippedNoDeduct",
            component: shippedNoDeductReport,
            meta: {
                breadcrumb: "wms.menu.shipped_no_deduct",
                requiresAuth: true
            }
        },
        {
            path: "inventory/transfer",
            name: "WmsInventoryTransfer",
            component: inventoryTransfer,
            meta: {
                breadcrumb: "wms.menu.inventory",
                requiresAuth: true
            }
        },
        {
            path: "inventory/return",
            name: "WmsReturnWarehouse",
            component: returnWarehouse,
            meta: {
                breadcrumb: "wms.menu.return_warehouse",
                requiresAuth: true
            }
        },
        {
            path: "inventory/transfertoline",
            name: "WmsInventoryTransferToLine",
            component: inventoryTransferToLine,
            meta: {
                breadcrumb: "wms.menu.inventory_to_line",
                requiresAuth: true
            }
        },
        {
            path: "partner",
            name: "WmsPartner",
            component: partnerList,
            meta: {
                breadcrumb: "wms.menu.partner",
                requiresAuth: true
            }
        },
        {
            path: "product",
            name: "WmsProduct",
            component: productList,
            meta: {
                breadcrumb: "wms.menu.product",
                requiresAuth: true
            }
        },
        {
            path: "productv2",
            name: "WmsProductV2",
            component: productListV2,
            meta: {
                breadcrumb: "wms.menu.product_v2",
                requiresAuth: true
            }
        },
        {
            path: "available-product",
            name: "WmsAvailableProduct",
            component: availableProductList,
            meta: {
                breadcrumb: "wms.menu.available_product",
                requiresAuth: true
            }
        },
        {
            path: "available-product/orders",
            name: "WmsAvailableProductPackingOrders",
            component: availableProductPackingOrders,
            meta: {
                breadcrumb: "wms.menu.available_product",
                requiresAuth: true
            }
        },
        {
            path: "product/waitship",
            name: "WmsProductWaitShip",
            component: productWaitShipList,
            meta: {
                breadcrumb: "wms.menu.product",
                requiresAuth: true
            }
        },
        {
            path: "storage",
            name: "WmsStorage",
            component: storageList,
            meta: {
                breadcrumb: "wms.menu.storage",
                requiresAuth: true
            }
        },
        {
            path: "forecast",
            name: "WmsProductForecast",
            component: productForecast,
            meta: {
                breadcrumb: "forecast.title",
                requiresAuth: true
            }
        },
        {
            path: "usage",
            name: "WmsProductUsage",
            component: productUsage,
            meta: {
                breadcrumb: "usage.title",
                requiresAuth: true
            }
        },
    ]
};

let boMenu = {
    path: "/backoffice",
    component: DashboardLayout,
    redirect: "/backoffice/orders",
    name: "BackOffice",
    meta: {
        breadcrumb: "backoffice"
    },
    children: [
        // coupon
        {
            path: "coupon",
            name: "BOCoupon",
            component: boCoupon,
            meta: {
                breadcrumb: "menu.coupon",
                requiresAuth: true
            }
        },
        {
            path: "coupon/detail/:id",
            name: "BOCouponDetail",
            component: boCouponDetail,
            meta: {
                breadcrumb: "menu.coupon",
                requiresAuth: true
            }
        },
        {
            path: "coupon/all/:id",
            name: "BOCouponDetailAll",
            component: boCouponDetailAll,
            meta: {
                breadcrumb: "menu.coupon",
                requiresAuth: true
            }
        },


        {
            path: "coupon/member/:id",
            name: "BOCouponMember",
            component: boCouponMember,
            meta: {
                breadcrumb: "menu.coupon",
                requiresAuth: true
            }
        },

        {
            path: "coupon/logs/:id?",
            name: "BOCouponLogs",
            component: boCouponLogs,
            meta: {
                breadcrumb: "menu.coupon_log",
                requiresAuth: true
            }
        },

        {
            path: "coupon/:action",
            name: "BOCouponForm",
            component: boCouponForm,
            meta: {
                breadcrumb: "menu.coupon",
                requiresAuth: true
            }
        },

        // PO
        {
            path: "po",
            name: "PO",
            component: boPO,
            meta: {
                breadcrumb: "menu.po",
                requiresAuth: true
            }
        },
        {
            path: "po/:action",
            name: "POForm",
            component: boPOForm,
            meta: {
                breadcrumb: "menu.po",
                requiresAuth: true
            }
        },
        {
            path: "orders",
            name: "BOOrders",
            component: boOrder,
            meta: {
                breadcrumb: "orders",
                requiresAuth: true
            }
        },
        {
            path: "orders/reprocess",
            name: "BOOrderReprocess",
            component: boOrderReprocess,
            meta: {
                // Use the leaf key — passing the parent ("reprocess") to
                // $t() returns the whole nested object as JSON in the
                // breadcrumb.
                breadcrumb: "reprocess.title",
                requiresAuth: true,
                requiresAdmin: true,
                // B7 (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md §3.2.1):
                // not kept alive with its tab — it polls batch / fix-status / subscribe jobs.
                keepAlive: false
            }
        },
        {
            // ภาพรวมรายการสั่งซื้อ — MadameFin staff only (#441, spec
            // 2026-10-09 §B2). Must stay before "orders/:id".
            path: "orders-overview",
            name: "BOOrdersOverview",
            component: boOrdersNew,
            meta: {
                breadcrumb: "orders_new.title",
                requiresAuth: true,
                requiresMdfStaff: true,
                // owner 2026-10-11: users-page right "ภาพรวมรายการสั่งซื้อ" (order_overview)
                requiresOrderOverview: true
            }
        },
        {
            // Old URL (bookmarks, taskbar tabs saved before #441). vue-router
            // carries the query over to the target, and the target is guarded.
            path: "orders-new",
            redirect: to => ({ path: "/backoffice/orders-overview", query: to.query, hash: to.hash })
        },
        {
            path: "orders/:id",
            // Was also "BOOrders" (a duplicate of the list route above): vue-router
            // keeps the first record for a name and warned on every boot. Every
            // push({ name: "BOOrders" }) in src means the LIST; this form is only
            // ever reached by path ("/backoffice/orders/" + id). Pinned by
            // tests/unit/routeNamesUnique.spec.js.
            name: "BOOrderEdit",
            component: boOrderForm,
            meta: {
                breadcrumb: "order_form",
                requiresAuth: true
            }
        },
        {
            path: "available-stock",
            name: "BOAvailableStock",
            component: availableProductView,
            meta: {
                breadcrumb: "wms.menu.available_product",
                requiresAuth: true
            }
        },
        {
            path: "ordersv2/:id",
            name: "BOOrdersQuick",
            component: boOrderFormQuick,
            meta: {
                breadcrumb: "order_form",
                requiresAuth: true
            }
        },
        {
            path: "products",
            name: "BOProduct",
            component: boProduct,
            meta: {
                breadcrumb: "products",
                requiresAuth: true
            }
        },

        {
            path: "packings",
            name: "BOPacking",
            component: boPacking,
            meta: {
                breadcrumb: "packings",
                requiresAuth: true
            }
        },
		// {
        //     path: "packings/shipping",
        //     name: "BOShipping",
        //     component: boShipping,
        //     meta: {
        //         breadcrumb: "shipping",
        //         requiresAuth: true
        //     }
        // },

		{
            path: "packings/shipping/checking",
            name: "BOShippingChecking",
            component: boShippingChecking,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true
            }
        },
		{
            path: "packings/shipping/coupon",
            name: "BOShippingCoupon",
            component: boShippingWithCoupon,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true
            }
        },
		{
            path: "packings/shipping/coupon/v2",
            name: "BOShippingCouponV2",
            component: boShippingWithCouponV2,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true
            }
        },
		{
            path: "packings/shipping/coupon/v3",
            name: "BOShippingCouponV3",
            component: boShippingWithCouponV3,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true,
                // B7 (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md §3.2.1):
                // not kept alive with its tab — it opens the webcam and records.
                keepAlive: false
            }
        },
		{
            path: "packings/shipping/line",
            name: "BOShippingLine",
            component: boShippingByLine,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true
            }
        },
		{
            path: "packings/shipping/autoclose",
            name: "BOShippingLineAutoClose",
            component: boShippingByLineAutoClose,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true,
				requiresAdmin: true
            }
        },
		{
            path: "packings/shipping/admin",
            name: "BOShippingAdmin",
            component: boShippingAdmin,
            meta: {
                breadcrumb: "shipping",
                requiresAuth: true
            }
        },
		{
            path: "packings/shipping/returnv0",
            name: "BOShippingReturn",
            component: boShippingReturn,
            meta: {
                breadcrumb: "shipping_return",
                requiresAuth: true,
                requiresOrderReturn: true
            }
        },
		{
            path: "packings/shipping/returnv2",
            name: "BOShippingReturnV2",
            component: boShippingReturnV2,
            meta: {
                breadcrumb: "shipping_return",
                requiresAuth: true,
                requiresOrderReturn: true
            }
        },
		{
            path: "packings/shipping/return",
            name: "BOShippingReturnV3",
            component: boShippingReturnV3,
            meta: {
                breadcrumb: "shipping_return",
                requiresAuth: true,
                requiresOrderReturn: true,
                // B7 (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md §3.2.1):
                // not kept alive with its tab — it opens the webcam and records.
                keepAlive: false
            }
        },

        {
            path: "payments",
            name: "BOPayment",
            component: boPayment,
            meta: {
                breadcrumb: "payments",
                requiresAuth: true
            }
        },
		{
            path: "customers",
            name: "BOCustomer",
            component: boCustomer,
            meta: {
                breadcrumb: "customer",
                requiresAuth: true
            }
        },
		{

            path: "reports/dashboard",
            // Keeps the name "ReportTeam" (first record; nothing pushes by it). The
            // reports/team record below was ALSO "ReportTeam" and is now ReportByTeam.
            name: "ReportTeam",
            component: reportDashboard,
            meta: {
                // Was "report", a key that exists in neither language file, so
                // the breadcrumb and the taskbar tab both fell back to the route
                // NAME and the dashboard introduced itself as "ReportTeam".
                // Reuses the key its own sidebar entry uses
                // (menu-config.js menu_dashboard_team) so the menu and the
                // breadcrumb cannot drift apart again.
                breadcrumb: "menu_dashboard_team",
                requiresAuth: true
            }
		},
        {
            path: "reports/dashboard-by-platform-channel",
            name: "ReportDashboardByPlatformChannel",
            component: reportDashboardByPlatformChannel,
            meta: {
                breadcrumb: "dashboard_v2",
                requiresAuth: true,
                requiresDashboardByPlatformChannel: true
            }
        },
        {
            path: "reports/team",
            name: "ReportByTeam",
            component: reportTeam,
            meta: {
                breadcrumb: "report_by_team",
                requiresAuth: true
            }
        },
        {
            path: "reports/sale-channel",
            name: "ReportSaleChannel",
            component: reportSaleChannel,
            meta: {
                breadcrumb: "report_by_sale_channel",
                requiresAuth: true
            }
        },
        {
            path: "reports/product",
            name: "ReportProduct",
            component: reportProduct,
            meta: {
                breadcrumb: "report_by_product",
                requiresAuth: true
            }
        },
        {
            // รายงานต้นทุน — the new SKU-level cost/sales report (#265, bo-api #594).
            path: "reports/product-cost",
            name: "ReportProductCost",
            component: reportProductCost,
            meta: {
                breadcrumb: "report_product_cost",
                requiresAuth: true,
                requiresProductCost: true
            }
        },
        {
            path: "reports/salechannel-product",
            name: "ReportSaleChannelAndProduct",
            component: reportSaleChannelAndProduct,
            meta: {
                // The sidebar's key for this very path is
                // report_by_sale_channel_and_product (menu-config.js
                // menu_report_salechannel_product); the "and" was dropped here
                // and the page showed the key itself.
                breadcrumb: "report_by_sale_channel_and_product",
                requiresAuth: true,
                // This report prints ต้นทุน per promotion and has never had a
                // gate on either side (spec §6.3 ข้อ 1/ข้อ 2 gates the API);
                // typing the URL by hand was enough to read it.
                requiresProductCost: true
            }
        },

        {
            path: "reports/user-product",
            name: "ReportUserAndProduct",
            component: reportUserAndProduct,
            meta: {
                breadcrumb: "report_by_user_product",
                requiresAuth: true
            }
        },
        {
            path: "reports/user",
            name: "ReportUser",
            component: reportUser,
            meta: {
                breadcrumb: "report_by_user",
                requiresAuth: true
            }
        },
        {
            path: "reports/summary-wms",
            name: "ReportSummaryWms",
            component: reportSummaryWms,
            meta: {
                breadcrumb: "report_wms_summary",
                requiresAuth: true
            }
        },
        {
            path: "reports/summary-wms-product",
            name: "ReportSummaryWmsProduct",
            component: reportSummaryWmsProduct,
            meta: {
                // th.js/en.js spell it report_wms_PRODUCT_SUMMARY, which is also
                // the key menu_report_wms_product_summary uses for this path.
                breadcrumb: "report_wms_product_summary",
                requiresAuth: true
            }
        },
        {
            path: "reports/summary-wms-field",
            name: "ReportSummaryWmsByField",
            component: reportSummaryWmsByField,
            meta: {
                // Same inversion as the report above: the languages and the
                // sidebar both say report_wms_FIELD_SUMMARY.
                breadcrumb: "report_wms_field_summary",
                requiresAuth: true
            }
        },
        {
            path: "reports/commission",
            name: "ReportCommission",
            component: reportCommission,
            meta: {
                breadcrumb: "report_commission",
                requiresAuth: true
            }
        },
        {
            path: "reports/order-return",
            name: "ReportOrderReturn",
            component: reportOrderReturn,
            meta: {
                breadcrumb: "report_order_return",
                requiresAuth: true
            }
        },
        // Agent Commission
        {
            path: "agent-commission/settings",
            name: "AgentCommissionSettings",
            component: agentCommissionSettings,
            meta: {
                breadcrumb: "agent_commission.menu.settings",
                requiresAuth: true,
                requiresAgentCommission: true
            }
        },
        {
            path: "agent-commission/rounds",
            name: "AgentCommissionRounds",
            component: agentCommissionRounds,
            meta: {
                breadcrumb: "agent_commission.menu.rounds",
                requiresAuth: true,
                requiresAgentCommission: true
            }
        },
        {
            path: "agent-commission/remittance",
            name: "AgentCommissionRemittance",
            component: agentCommissionRemittance,
            meta: {
                breadcrumb: "agent_commission.menu.remittance",
                requiresAuth: true,
                requiresAgentCommission: true,
                // B7 round 2 (spec §2.1, owner: "opt-out ทำทั้ง 2 หน้าเลย"):
                // the import poller (setInterval) must not run in a hidden tab.
                keepAlive: false
            }
        },
        {
            path: "agent-commission/rounds/:id",
            name: "AgentCommissionRoundDetail",
            component: agentCommissionRoundDetail,
            meta: {
                breadcrumb: "agent_commission.menu.round_detail",
                requiresAuth: true,
                requiresAgentCommission: true
            }
        },

        // Platform Statement
        {
            path: "platform-statement",
            name: "PlatformStatementList",
            component: platformStatementList,
            meta: {
                breadcrumb: "platform_statement.list",
                requiresAuth: true
            }
        },
        {
            path: "platform-statement/upload",
            name: "PlatformStatementUpload",
            component: platformStatementUpload,
            meta: {
                breadcrumb: "platform_statement.upload",
                requiresAuth: true
            }
        },
        {
            path: "platform-statement/summary",
            name: "PlatformStatementSummary",
            component: platformStatementSummary,
            meta: {
                breadcrumb: "platform_statement.summary",
                requiresAuth: true
            }
        },
        {
            path: "platform-statement/:id",
            name: "PlatformStatementDetail",
            component: platformStatementDetail,
            meta: {
                breadcrumb: "platform_statement.detail",
                requiresAuth: true
            }
        },

        // Shipping Config (config.yml -> DB)
        {
            path: "shipping-config",
            name: "ShippingConfig",
            component: shippingConfig,
            meta: {
                breadcrumb: "shipping_config.title",
                requiresAuth: true
            }
        },


    ]
};

let profileMenu = {
    path: "/profile",
    component: DashboardLayout,
    redirect: "/profile/me",
    name: "profile`",
    meta: {
        breadcrumb: "profile.info"
    },
    children: [
        {
            path: "me",
            name: "ProfileChange",
            component: ProfileChangeProfile,
            meta: {
                breadcrumb: "profile.info",
                requiresAuth: true
            }
        },
        {
            path: "changepasword",
            name: "ProfileChangePassword",
            component: ProfileChangePassword,
            meta: {
                breadcrumb: "profile.change_password",
                requiresAuth: true
            }
        },
        {
            path: "menu-settings",
            name: "ProfileMenuSettings",
            component: ProfileMenuSettings,
            meta: {
                breadcrumb: "menu_pref.title",
                requiresAuth: true
            }
        }
    ]
};

let pagesMenu = {
    path: "/pages",
    component: DashboardLayout,
    name: "Pages",
    redirect: "/pages/user",
    children: [
        {
            path: "user",
            name: "User Page",
            component: User
        },
        {
            path: "timeline",
            name: "Timeline Page",
            component: TimeLine
        }
    ]
};

let adminMenu = {
    path: "/admin",
    component: DashboardLayout,
    name: "Admin",
    redirect: "/admin/user",
    meta: {
        breadcrumb: "bc_admin"
    },
    children: [
        {
            // path/component before meta (was meta-first), like the other
            // keepAlive opt-outs. B7 round 2 (docs/specs/2026-10-06-bo-taskbar-
            // fill-keepalive-design.md §2.1, owner: "opt-out ทำทั้ง 2 หน้าเลย"):
            // the page polls (Amaze health + sync-job setInterval), so a hidden
            // kept-alive instance would keep polling.
            path: "platform",
            name: "platform",
            component: platformConnect,
            meta: {
                breadcrumb: "platform_connect",
                requiresAuth: true,
                requiresAdmin: true,
                keepAlive: false
            }
        },
        {
            meta: {
                breadcrumb: "sync.log.breadcrumb",
					requiresAuth: true,
					requiresAdmin: true
            },
            path: "platform/sync-logs",
            name: "platform_sync_logs",
            component: platformSyncLogs
        },
        {
            meta: {
                breadcrumb: "issue_report.list.breadcrumb",
                requiresAuth: true
            },
            path: "issue-reports",
            name: "issue_reports",
            component: issueReports
        },
        {
            meta: {
                breadcrumb: "issue_report.form.breadcrumb",
                requiresAuth: true
            },
            path: "issue-reports/new",
            name: "issue_report_new",
            component: issueReportForm
        },
        {
            meta: {
                breadcrumb: "release.breadcrumb",
                requiresAuth: true
            },
            path: "releases",
            name: "releases",
            component: releases
        },
        {
            // Renamed 2026-10-07 (owner): "productv2" -> "product_platform" under /admin.
            // Pinned taskbar tabs and bookmarks still carry the old path.
            path: "productv2",
            redirect: "/admin/product_platform"
        },
        {
            meta: {
                breadcrumb: "menu.user",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "user",
            name: "user",
            component: adminUser
        },
		{
            meta: {
                breadcrumb: "sale_channel",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "sale-channel",
            name: "sale-channel",
            component: adminSaleChannel
        },
		{
            meta: {
                breadcrumb: "product",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "product",
            name: "product",
            component: adminProduct
        },
		{
            meta: {
                breadcrumb: "product_form",
				requiresAuth: true,
				requiresAdmin: true
            },
            // §5 B1 (spec 2026-10-07-bo-form-draft-clear-design.md): create =
            // /admin/product/form, edit = /admin/product/form/:id — one taskbar
            // tab per product; the form loads the product by this id.
            path: "product/form/:id?",
            name: "product_form",
            component: adminProductForm
        },
		{
            meta: {
                breadcrumb: "product",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "product/:platform",
            name: "productPlatform",
            component: adminPlatformProduct
        },
		{
            meta: {
                breadcrumb: "menu.admin_product_v2",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "product_platform",
            name: "product_v2",
            component: adminPlatformProductV2
        },
		{
            meta: {
                breadcrumb: "product_form",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "product/:platform/:id",
            name: "platform_product_form",
            component: adminPlatformProductForm
        },
		{
            meta: {
                breadcrumb: "payment_channel",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "payment",
            name: "payment",
            component: adminPaymentChannel
        },
		{
            meta: {
                // "sku_random" was never written to th.js/en.js — the sidebar
                // entry (menu_admin_skurandom) has always used this key.
                breadcrumb: "menu.admin_skurandom",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "skurandom",
            name: "skurandom",
            component: adminSkuRandom
        },
		{
            meta: {
                breadcrumb: "coupon",
				requiresAuth: true,
				requiresAdmin: true
            },
            path: "coupon",
            name: "coupon",
            component: adminCoupon
        },
    ]
};

let accountMenu = {
    path: "/account",
    component: DashboardLayout,
    redirect: "/account/records",
    name: "Account",
    meta: {
        breadcrumb: "account.name"
    },
    children: [
        {
            path: "items",
            name: "AccountItems",
            component: accountItems,
            meta: {
                breadcrumb: "account.menu.items",
                requiresAuth: true
            }
        },
        {
            path: "records",
            name: "AccountRecords",
            component: accountRecords,
            meta: {
                breadcrumb: "account.menu.records",
                requiresAuth: true
            }
        },
        {
            path: "reports",
            name: "AccountReports",
            component: accountReports,
            meta: {
                breadcrumb: "account.menu.reports",
                requiresAuth: true
            }
        }
    ]
};

let memberAdminMenu = {
    path: "/members",
    component: DashboardLayout,
    name: "MemberManagement",
    meta: { breadcrumb: "member_admin.title" },
    children: [
        {
            path: "",
            name: "MemberAdminList",
            component: memberAdminList,
            meta: { breadcrumb: "member_admin.title", requiresAuth: true },
        },
        {
            path: "levels",
            name: "MemberLevels",
            component: memberAdminLevels,
            meta: { breadcrumb: "member_level.title", requiresAuth: true },
        },
        {
            // Coin liability report (coin D3-4). Declared before the ":id"
            // catch-all below, otherwise "coins-report" is read as a member id.
            path: "coins-report",
            name: "MemberCoinsReport",
            component: memberCoinsReport,
            meta: { breadcrumb: "member_coins.liability.title", requiresAuth: true },
        },
        {
            // COD-rejection anti-abuse guard (issue #179)
            path: "cod-rejections",
            name: "MemberCodRejections",
            component: memberCodRejections,
            meta: { breadcrumb: "cod_rejections.title", requiresAuth: true },
        },
        {
            path: ":id",
            name: "MemberAdminDetail",
            component: memberAdminDetail,
            meta: { breadcrumb: "member_admin.detail", requiresAuth: true },
        },
    ],
};

let kolMenu = {
    path: "/kol",
    component: DashboardLayout,
    redirect: "/kol/assignments",
    name: "KOL",
    meta: {
        breadcrumb: "kol.name"
    },
    children: [
        {
            path: "staff",
            name: "KOLStaff",
            component: kolStaff,
            meta: {
                breadcrumb: "kol.menu.staff",
                requiresAuth: true
            }
        },
        {
            path: "creators",
            name: "KOLCreators",
            component: kolCreators,
            meta: {
                breadcrumb: "kol.menu.creators",
                requiresAuth: true
            }
        },
        {
            path: "assignments",
            name: "KOLAssignments",
            component: kolAssignments,
            meta: {
                breadcrumb: "kol.menu.assignments",
                requiresAuth: true
            }
        },
        {
            path: "product-sends",
            name: "KOLProductSends",
            component: kolProductSends,
            meta: {
                breadcrumb: "kol.menu.product_sends",
                requiresAuth: true
            }
        },
        {
            path: "reports",
            name: "KOLReports",
            component: kolReports,
            meta: {
                breadcrumb: "kol.menu.reports",
                requiresAuth: true
            }
        },
        {
            path: "payout-batches",
            name: "KOLPayoutBatches",
            component: kolPayoutBatches,
            meta: {
                breadcrumb: "kol.menu.payout_batches",
                requiresAuth: true
            }
        },
        {
            path: "payout-batches/:id",
            name: "KOLPayoutBatchDetail",
            component: kolPayoutBatchDetail,
            meta: {
                breadcrumb: "kol.payout_batch_detail",
                requiresAuth: true
            }
        }
    ]
};

// The seller catalogue pages (spec docs/specs/2026-10-03-seller-ui-revamp-design.md §2).
//
// These used to be the children of a route object of their own at
// `/app-catalog`. They are now CHILDREN OF `sellerMenu` (`/seller`, below) —
// there must be exactly ONE route object per path, because vue-router 3 matches
// the first `/seller` object it finds and would never look at a second one, so
// two objects would have silently hidden half the menu.
//
// Only the `path` strings changed; every `name` is untouched on purpose
// (`src/views/Admin/components/ShopeeBulkSyncModal.vue` and
// `ShopeeToSellerModal.vue` navigate by `{ name: "AppCatalogProductEdit" }`),
// and the view files stay in `src/views/AppCatalog/`.
let appCatalogChildren = [
        {
            path: "categories",
            name: "AppCatalogCategories",
            component: appCatalogCategories,
            meta: {
                breadcrumb: "menu.app_catalog_categories",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "products",
            name: "AppCatalogProducts",
            component: appCatalogProducts,
            meta: {
                breadcrumb: "menu.app_catalog_products",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "products/:id/edit",
            name: "AppCatalogProductEdit",
            component: appCatalogProductEdit,
            meta: {
                breadcrumb: "menu.app_catalog_products",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            // #301 — "SKU ที่ยังไม่ได้แมปคลัง", under ผู้ขาย → สินค้า. Same access
            // rules as the product list it is reached from: it only reads the
            // seller catalogue's mapping health.
            path: "stock-unmatched",
            name: "AppCatalogStockUnmatched",
            component: appCatalogStockUnmatched,
            meta: {
                breadcrumb: "menu.app_catalog_stock_unmatched",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            // แฟลชเซล (spec 2026-10-05-flash-sale-campaign-page §2): the list moved
            // from /seller/promotions to /seller/flash-sales. Route NAME unchanged.
            path: "flash-sales",
            name: "AppCatalogPromotions",
            component: appCatalogPromotions,
            meta: {
                breadcrumb: "menu.app_catalog_promotions",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            // Old bookmarks, taskbar pins saved with the old path, and
            // sellerPath('/app-catalog/promotions') all still land on the list.
            path: "promotions",
            redirect: "/seller/flash-sales"
        },
        {
            // Before :id on purpose (the digits-only regex already keeps "new" out).
            path: "flash-sales/new",
            name: "AppCatalogFlashSaleNew",
            component: appCatalogFlashSaleEdit,
            meta: {
                breadcrumb: "app_catalog.flash_sale_new_title",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "flash-sales/:id(\\d+)",
            name: "AppCatalogFlashSaleEdit",
            component: appCatalogFlashSaleEdit,
            meta: {
                breadcrumb: "app_catalog.flash_sale_edit_breadcrumb",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "bundles",
            name: "AppCatalogBundles",
            component: appCatalogBundles,
            meta: {
                breadcrumb: "menu.app_catalog_bundles",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "bundles/:id/edit",
            name: "AppCatalogBundleEdit",
            component: appCatalogBundleEdit,
            meta: {
                breadcrumb: "menu.app_catalog_bundles",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "coupons",
            name: "AppCatalogCouponTemplates",
            component: appCatalogCouponTemplates,
            meta: {
                breadcrumb: "menu.app_catalog_coupons",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            // #263 — "จับคู่หมวด platform", under ตั้งค่าร้านค้า in the sidebar.
            path: "platform-category-map",
            name: "AppCatalogPlatformCategoryMap",
            component: appCatalogPlatformCategoryMap,
            meta: {
                breadcrumb: "menu.app_catalog_platform_category_map",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            // #282 — "จับคู่หมวด Shopee → seller", next to จับคู่หมวด platform
            // under ตั้งค่าร้านค้า. Same access rules: it edits seller
            // categories' mapping, nothing platform-side.
            path: "shopee-category-map",
            name: "AppCatalogShopeeCategoryMap",
            component: appCatalogShopeeCategoryMap,
            meta: {
                breadcrumb: "shopee_category_map.menu",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            // B7 — "โปรโมชั่น Shopee → seller", under ตั้งค่าร้านค้า next to the
            // category map. Same access rules and the same reason: a promotion
            // spans many listings, so it is store-level work, not per-product.
            path: "shopee-promotions",
            name: "AppCatalogShopeePromotions",
            component: appCatalogShopeePromotions,
            meta: {
                breadcrumb: "shopee_promotion.menu_title",
                requiresAuth: true,
                requiresSeller: true,
                // B7 (docs/specs/2026-10-06-bo-taskbar-fill-keepalive-design.md §3.2.1):
                // not kept alive with its tab — it polls sync progress.
                keepAlive: false
            }
        },
        {
            path: "storefront",
            name: "AppCatalogStorefront",
            component: appCatalogStorefront,
            meta: {
                breadcrumb: "menu.app_catalog_storefront",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "storefront-home",
            name: "AppCatalogStorefrontHome",
            component: appCatalogStorefrontHome,
            meta: {
                breadcrumb: "menu.app_catalog_storefront_home",
                requiresAuth: true,
                requiresAdmin: true
            }
        },
        {
            // www collections + discovery sets (spec 2026-10-13 §5.2)
            path: "storefront-collections",
            name: "AppCatalogStorefrontCollections",
            component: appCatalogStorefrontCollections,
            meta: {
                breadcrumb: "menu.app_catalog_storefront_collections",
                requiresAuth: true,
                requiresAdmin: true
            }
        },
        {
            path: "shipping-tiers",
            name: "AppCatalogShippingTiers",
            component: appCatalogShippingTiers,
            meta: {
                breadcrumb: "menu.app_catalog_shipping_tiers",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "wms-products",
            name: "AppCatalogWmsProducts",
            component: appCatalogWmsProducts,
            meta: {
                breadcrumb: "menu.app_catalog_wms_products",
                requiresAuth: true,
                requiresSeller: true
            }
        }
];

// Seller menu (#46): umbrella over App Catalog + new Orders + Payment Report.
// Orders.vue is a STUB owned by Dev 2 (Seller Orders view, #48).
let sellerMenu = {
    path: "/seller",
    component: DashboardLayout,
    redirect: "/seller/orders",
    name: "AppSeller",
    meta: {
        breadcrumb: "menu.seller"
    },
    children: [
        {
            path: "orders",
            name: "AppSellerOrders",
            component: sellerOrders,
            meta: {
                breadcrumb: "menu.seller_orders",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        {
            path: "payment-report",
            name: "AppSellerPaymentReport",
            component: sellerPaymentReport,
            meta: {
                breadcrumb: "menu.seller_payment_report",
                requiresAuth: true,
                requiresSeller: true
            }
        },
        // The catalogue pages, moved here from the retired `/app-catalog` route
        // object (spec §2). Appended, not interleaved: `orders` and
        // `payment-report` keep the paths they already had.
        ...appCatalogChildren
    ]
};

// Old bookmarks and anything already printed on paper (spec §1, edge case 1).
//
// A FUNCTION redirect, not a string one: `/app-catalog/products/42/edit?tab=skus#sku-7`
// has to arrive at the same tab and the same anchor, and a string redirect would
// throw both away. No `component` either — a component here would mount
// DashboardLayout for one frame and flash an empty page before the real route.
//
// Registered where `appCatalogMenu` used to be, i.e. BEFORE pagesMenu's `*`
// catch-all, which would otherwise swallow these URLs into the 404 page.
let legacyAppCatalogRedirect = {
    path: "/app-catalog*",
    redirect: to => ({
        path: sellerPath(to.path),
        query: to.query,
        hash: to.hash
    })
};

// #443 POS หน้าร้าน — BO pages. Admin pages: meta.requiresPosAdmin; bills and
// reports: meta.requiresPosSupervisor (guards in router.js). Registered before
// authPages, whose children hold the `*` catch-all.
let posMenu = {
    path: "/pos",
    component: DashboardLayout,
    redirect: "/pos/bills",
    name: "Pos",
    meta: {
        breadcrumb: "pos.menu.root"
    },
    children: [
        {
            path: "devices",
            name: "PosDevices",
            component: posDevices,
            meta: {
                breadcrumb: "pos.menu.devices",
                requiresAuth: true,
                requiresPosBOSupervisor: true // §13.1 pos_bo + level 2 (phase 3 R-X)
            }
        },
        {
            path: "events",
            name: "PosEvents",
            component: posEvents,
            meta: {
                breadcrumb: "pos.menu.events",
                requiresAuth: true,
                requiresPosBOAdmin: true
            }
        },
        {
            path: "events/:id",
            name: "PosEventForm",
            component: posEventForm,
            meta: {
                breadcrumb: "pos.menu.events",
                requiresAuth: true,
                requiresPosBOAdmin: true
            }
        },
        {
            path: "products",
            name: "PosProducts",
            component: posProducts,
            meta: {
                breadcrumb: "pos.menu.products",
                requiresAuth: true,
                requiresPosBOAdmin: true
            }
        },
        {
            // phase 3 (R-E): bundles became POS products; old bookmarks land there
            path: "bundles",
            redirect: "/pos/products"
        },
        {
            path: "settings",
            name: "PosSettings",
            component: posSettings,
            meta: {
                breadcrumb: "pos.menu.settings",
                requiresAuth: true,
                requiresPosBOAdmin: true
            }
        },
        {
            path: "bills",
            name: "PosBills",
            component: posBills,
            meta: {
                breadcrumb: "pos.menu.bills",
                requiresAuth: true,
                requiresPosBO: true
            }
        },
        {
            path: "reports/sales",
            name: "PosReportSales",
            component: posReportSales,
            meta: {
                breadcrumb: "pos.menu.report_sales",
                requiresAuth: true,
                requiresPosBO: true
            }
        },
        {
            path: "reports/top-spenders",
            name: "PosReportTopSpenders",
            component: posReportTopSpenders,
            meta: {
                breadcrumb: "pos.menu.report_top_spenders",
                requiresAuth: true,
                requiresPosBO: true
            }
        },
        {
            path: "reports/customers/:phone",
            name: "PosReportCustomer",
            component: posReportCustomer,
            meta: {
                breadcrumb: "pos.menu.report_top_spenders",
                requiresAuth: true,
                requiresPosBO: true
            }
        },
        {
            path: "reports/products",
            name: "PosReportProducts",
            component: posReportProducts,
            meta: {
                breadcrumb: "pos.menu.report_products",
                requiresAuth: true,
                requiresPosBO: true
            }
        },
        {
            path: "reports/promotions",
            name: "PosReportPromotions",
            component: posReportPromotions,
            meta: {
                breadcrumb: "pos.menu.report_promotions",
                requiresAuth: true,
                requiresPosBO: true
            }
        },
        {
            path: "reports/event-members",
            name: "PosReportEventMembers",
            component: posReportEventMembers,
            meta: {
                breadcrumb: "pos.menu.report_event_members",
                requiresAuth: true,
                requiresPosBO: true
            }
        }
    ]
};

// การตลาด (Phase 1 ads, spec 2026-10-13 §6.1): landing pages + source report.
const marketingLandingPages = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/LandingPageList.vue");
const marketingLandingPageEdit = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/LandingPageEdit.vue");
const marketingAttributionReport = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AttributionReport.vue");
// Phase 2 referral codes (spec 2026-10-13-phase2 §5.1).
const marketingReferralCodes = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/ReferralCodeList.vue");
const marketingReferralReport = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/ReferralReport.vue");
// Phase 3 affiliate (spec 2026-10-13 §5)
const marketingAffiliateSettings = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AffiliateSettings.vue");
const marketingAffiliateMembers = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AffiliateMembers.vue");
const marketingAffiliateCommissions = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AffiliateCommissions.vue");
const marketingAffiliatePayouts = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AffiliatePayouts.vue");
const marketingAffiliatePayoutDetail = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AffiliatePayoutDetail.vue");
const marketingAffiliateReport = () =>
    import(/* webpackChunkName: "marketing" */ "@/views/Marketing/AffiliateReport.vue");

let marketingMenu = {
    path: "/marketing",
    component: DashboardLayout,
    // Phase 3: a finance-only user (no marketing role) lands on the affiliate
    // members page instead of bouncing off the landing-page list. The store is
    // required lazily (store → http → router → routes would be a cycle).
    redirect: () => marketingHomePath(require("../store").default.getters),
    name: "Marketing",
    meta: {
        breadcrumb: "marketing.menu.root"
    },
    children: [
        {
            path: "landing-pages",
            name: "MarketingLandingPages",
            component: marketingLandingPages,
            meta: {
                breadcrumb: "marketing.menu.landing_pages",
                requiresAuth: true,
                requiresMarketing: true
            }
        },
        {
            path: "landing-pages/:id",
            name: "MarketingLandingPageEdit",
            component: marketingLandingPageEdit,
            meta: {
                breadcrumb: "marketing.menu.landing_pages",
                requiresAuth: true,
                requiresMarketing: true
            }
        },
        {
            path: "reports/attribution",
            name: "MarketingAttributionReport",
            component: marketingAttributionReport,
            meta: {
                breadcrumb: "marketing.menu.attribution",
                requiresAuth: true,
                requiresMarketing: true
            }
        },
        {
            path: "referral-codes",
            name: "MarketingReferralCodes",
            component: marketingReferralCodes,
            meta: {
                breadcrumb: "marketing.menu.referral_codes",
                requiresAuth: true,
                requiresMarketing: true
            }
        },
        {
            path: "reports/referral",
            name: "MarketingReferralReport",
            component: marketingReferralReport,
            meta: {
                breadcrumb: "marketing.menu.referral_report",
                requiresAuth: true,
                requiresMarketing: true
            }
        },
        {
            path: "affiliate/settings",
            name: "MarketingAffiliateSettings",
            component: marketingAffiliateSettings,
            meta: {
                breadcrumb: "marketing.menu.affiliate_settings",
                requiresAuth: true,
                requiresMarketing: true
            }
        },
        {
            path: "affiliate/members",
            name: "MarketingAffiliateMembers",
            component: marketingAffiliateMembers,
            meta: {
                breadcrumb: "marketing.menu.affiliate_members",
                requiresAuth: true,
                requiresMarketingOrAffiliateFinance: true
            }
        },
        {
            path: "affiliate/commissions",
            name: "MarketingAffiliateCommissions",
            component: marketingAffiliateCommissions,
            meta: {
                breadcrumb: "marketing.menu.affiliate_commissions",
                requiresAuth: true,
                requiresMarketingOrAffiliateFinance: true
            }
        },
        {
            path: "affiliate/payouts",
            name: "MarketingAffiliatePayouts",
            component: marketingAffiliatePayouts,
            meta: {
                breadcrumb: "marketing.menu.affiliate_payouts",
                requiresAuth: true,
                requiresAffiliateFinance: true
            }
        },
        {
            path: "affiliate/payouts/:id",
            name: "MarketingAffiliatePayoutDetail",
            component: marketingAffiliatePayoutDetail,
            meta: {
                breadcrumb: "marketing.menu.affiliate_payouts",
                requiresAuth: true,
                requiresAffiliateFinance: true
            }
        },
        {
            path: "reports/affiliate",
            name: "MarketingAffiliateReport",
            component: marketingAffiliateReport,
            meta: {
                breadcrumb: "marketing.menu.affiliate_report",
                requiresAuth: true,
                requiresMarketingOrAffiliateFinance: true
            }
        }
    ]
};

let modernTradeMenu = {
    path: "/modern-trade",
    component: DashboardLayout,
    redirect: "/modern-trade/companies",
    name: "ModernTrade",
    meta: {
        breadcrumb: "modern_trade.name"
    },
    children: [
        {
            path: "companies",
            name: "MtCompanies",
            component: mtCompanies,
            meta: {
                breadcrumb: "modern_trade.menu.companies",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "receipts",
            name: "MtReceipts",
            component: mtReceipts,
            meta: {
                breadcrumb: "modern_trade.menu.receipts",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "receipts/:id",
            name: "MtReceiptDetail",
            component: mtReceiptDetail,
            meta: {
                breadcrumb: "modern_trade.menu.receipts",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "sales-import",
            name: "MtSalesImport",
            component: mtSalesImport,
            meta: {
                breadcrumb: "modern_trade.menu.sales_import",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "stock",
            name: "MtStock",
            component: mtStock,
            meta: {
                breadcrumb: "modern_trade.menu.stock",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "expiry-report",
            name: "MtExpiryReport",
            component: mtExpiryReport,
            meta: {
                breadcrumb: "modern_trade.menu.expiry_report",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "expiry-returns",
            name: "MtExpiryReturns",
            component: mtExpiryReturns,
            meta: {
                breadcrumb: "modern_trade.menu.expiry_returns",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "products",
            name: "MtProducts",
            component: mtProducts,
            meta: {
                breadcrumb: "modern_trade.menu.products",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "price-templates",
            name: "MtPriceTemplates",
            component: mtPriceTemplates,
            meta: {
                breadcrumb: "modern_trade.menu.price_templates",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "sale-orders",
            name: "MtSaleOrders",
            component: mtSaleOrders,
            meta: {
                breadcrumb: "modern_trade.menu.sale_orders",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        // Declared before sale-orders/:id so "new" is never read as an id.
        // Kept alive per taskbar tab like any page (keyed by path, so /5/edit
        // and /6/edit are separate instances); MtSaleOrderEdit re-initialises
        // itself on every re-activation so it never shows stale state.
        {
            path: "sale-orders/new",
            name: "MtSaleOrderNew",
            component: mtSaleOrderEdit,
            meta: {
                breadcrumb: "modern_trade.menu.sale_orders",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "sale-orders/:id/edit",
            name: "MtSaleOrderEdit",
            component: mtSaleOrderEdit,
            meta: {
                breadcrumb: "modern_trade.menu.sale_orders",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "sale-orders/:id",
            name: "MtSaleOrderDetail",
            component: mtSaleOrderDetail,
            meta: {
                breadcrumb: "modern_trade.menu.sale_orders",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "consignment-payments",
            name: "MtConsignmentPayments",
            component: mtConsignmentPayments,
            meta: {
                breadcrumb: "modern_trade.menu.consignment_payments",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "invoices",
            name: "MtInvoices",
            component: mtInvoices,
            meta: {
                breadcrumb: "modern_trade.menu.invoices",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "reports/sales",
            name: "MtSalesReport",
            component: mtSalesReport,
            meta: {
                breadcrumb: "modern_trade.menu.report_sales",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        {
            path: "reports/stock-value",
            name: "MtStockValueReport",
            component: mtStockValueReport,
            meta: {
                breadcrumb: "modern_trade.menu.report_stock_value",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        // #166
        {
            path: "doc-format-settings",
            name: "MtDocFormatSettings",
            component: mtDocFormatSettings,
            meta: {
                breadcrumb: "modern_trade.menu.doc_format",
                requiresAuth: true,
                requiresModernTrade: true
            }
        },
        // round-19 Dev-B — MT audit log
        {
            path: "audit-log",
            name: "MtAuditLog",
            component: mtAuditLog,
            meta: {
                breadcrumb: "modern_trade.menu.audit",
                requiresAuth: true,
                requiresModernTrade: true
            }
        }
    ]
};

// #183 — full-screen print-set preview page. Top-level route (no DashboardLayout
// wrapper / sidebar) so the PDF preview + sidebar can use the whole viewport.
let mtPrintPreviewRoute = {
    path: "/modern-trade/print-preview/:soId",
    name: "MtPrintPreview",
    component: mtPrintPreview,
    meta: {
        // No sidebar entry (the page is opened from a SO), and being a TOP-LEVEL
        // route it has no parent record to inherit a breadcrumb from — so with
        // no key of its own the taskbar tab read "MtPrintPreview". Borrows the
        // page's own heading, modern_trade.print_preview.title.
        breadcrumb: "modern_trade.print_preview.title",
        requiresAuth: true,
        requiresModernTrade: true
    }
};

// #169 — promo notification admin (broadcast). Menu placement TBD by D3
// (suggest under the Admin/Backoffice group — see report).
let notificationMenu = {
    path: "/notification",
    component: DashboardLayout,
    redirect: "/notification/promo",
    name: "Notification",
    meta: {
        breadcrumb: "promo_notification.title"
    },
    children: [
        {
            path: "promo",
            name: "PromoNotificationAdmin",
            component: promoNotificationAdmin,
            meta: {
                breadcrumb: "promo_notification.title",
                requiresAuth: true
            }
        }
    ]
};

let authPages = {
    path: "/",
    component: AuthLayout,
    name: "Authentication",
    children: [
        {
            path: "/login",
            name: "Login",
            component: Login,
            meta: {
                noBodyBackground: true
            }
        },
        {
            path: "/forgotpassword",
            name: "forgot_password",
            component: ForgotPassword,
            meta: {
                noBodyBackground: true
            }
        },
        {
            path: "/resetpassword",
            name: "reset_password",
            component: ResetPassword,
            meta: {
                noBodyBackground: true
            }
        },
        {
            // Must stay inside authPages (no requiresAuth): the LINE *login*
            // flow lands here with no session at all. The LINE *link* flow also
            // lands here while authenticated, and the API — not this route — is
            // what checks the JWT on POST /api/v1/profile/line/link.
            // Also must stay ABOVE the `*` NotFound entry below.
            path: "/auth/line/callback",
            name: "LineCallback",
            component: LineCallback,
            meta: {
                noBodyBackground: true
            }
        },
        {
            path: "/home",
            name: "Home",
            component: Login
        },
        {
            path: "/register",
            name: "Register",
            component: Register
        },
        {
            path: "/pricing",
            name: "Pricing",
            component: Pricing
        },
        {
            path: "/lock",
            name: "Lock",
            component: Lock
        },

        { path: "*", component: NotFound }
    ]
};

const routes = [
    wmsMenu,
    boMenu,
    profileMenu,
    adminMenu,
    accountMenu,
    kolMenu,
    memberAdminMenu,
    sellerMenu,
    legacyAppCatalogRedirect,
    modernTradeMenu,
    mtPrintPreviewRoute, // #183
    notificationMenu, // #169
    posMenu, // #443 POS
    marketingMenu, // Phase 1 ads (landing pages + source report)

    // componentsMenu,
    // formsMenu,
    // tablesMenu,
    // mapsMenu,
    pagesMenu,
    {
        path: "/",
        component: DashboardLayout,
        redirect: "/backoffice",
        name: "Dashboard"
    },
    authPages
];

export default routes;
